/**
 * RequirementsTab - Shows all triggered requirements based on vessel configuration
 * Useful for understanding what additional requirements apply to the vessel
 */

import React, { useEffect, useState } from 'react';
import { getHardcodedVesselRequirements } from '../../../../config/hardcodedVesselRequirements';
import { AlertCircle, FileText, Shield, Book, Award, Ban, Clipboard, Activity, Database, GitCompare, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import RequirementsDebug from './RequirementsDebug';
import RequirementsDiffPanel from '../../requirements/RequirementsDiffPanel';
import { evaluateOpenFlame } from '../../../../services/requirements/evaluators/openFlameProtection';

const RequirementsTab = ({ vesselConfig }) => {
  const [requirements, setRequirements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDebug, setShowDebug] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('current');

  // Evaluate open-flame protection if applicable
  const openFlameEvaluation = React.useMemo(() => {
    if (vesselConfig?.hasGalley && 
        ['gas', 'open_flame', 'liquid_fuel'].includes(vesselConfig?.galleyHeatSource)) {
      return evaluateOpenFlame(vesselConfig);
    }
    return null;
  }, [vesselConfig]);

  // Process requirements to reorganize into Option A: strict module alignment
  const processedRequirements = React.useMemo(() => {
    if (!requirements) return {};
    
    // Split documentation into proper categories
    const allDocumentation = requirements.documentation || [];
    const crewQualifications = allDocumentation.filter(item => 
      item.category?.includes('Master') || 
      item.category?.includes('Second Person') ||
      item.category?.includes('Radio Certificates') ||
      item.category?.includes('Medical')
    );
    const vesselDocumentation = allDocumentation.filter(item =>
      (item.category?.includes('Manning') ||
       item.category?.includes('Survey') ||
       item.category?.includes('Certificates')) &&
      !item.category?.includes('Master') && 
      !item.category?.includes('Second Person') && 
      !item.category?.includes('Radio Certificates') &&
      !item.category?.includes('Medical') &&
      !item.category?.includes('Training') &&
      !item.category?.includes('Familiarization') &&
      !item.category?.includes('Procedures')
    );
    
    // Option A: Strict module alignment
    const equipmentRequirements = [
      ...(requirements.basicEquipment || []),
      ...(requirements.equipment || []),
      ...(requirements.areaCategoryEquipment || [])
    ];
    
    const crewQualificationsAndCertifications = [
      ...crewQualifications,
      ...(requirements.crewCertificationByArea || []),
      ...(requirements.basicCertificates || [])
    ];
    
    const vesselDocumentationAndRecords = [
      ...(requirements.basicDocumentation || []),
      ...vesselDocumentation.filter(item =>
        item.category !== 'Risk Assessments' &&
        !item.category?.includes('Risk Assessment') &&
        !item.id?.includes('.ra.') &&
        !item.name?.toLowerCase().includes('risk assessment')
      ),
      ...(requirements.certificates || [])
    ];
    
    // Separate procedures, training, and risk assessments properly
    const operationalProcedures = [
      ...(requirements.procedures || []).filter(item =>
        !item.category?.includes('Training') && 
        !item.category?.includes('Risk') &&
        !item.category?.includes('Policy')
      )
    ];
    
    const trainingRequirements = [
      ...(requirements.training || []),
      ...(requirements.crewCertificationByArea || []).filter(item =>
        item.category?.includes('Training') || 
        item.category?.includes('Familiarization')
      ),
      ...(requirements.procedures || []).filter(item => 
        item.category?.includes('Training')
      )
    ];
    
    const riskAssessments = [
      ...(requirements.riskAssessments || []),
      ...(requirements.procedures || []).filter(item => 
        item.category?.includes('Risk')
      ),
      // Extract risk assessments from documentation (they're mixed in there)
      ...allDocumentation.filter(item =>
        item.category === 'Risk Assessments' ||
        item.category?.includes('Risk Assessment') ||
        item.id?.includes('.ra.') ||
        item.name?.toLowerCase().includes('risk assessment')
      )
    ];
    
    // Check for policy-related items (procedures can include policies)
    const policiesAndProcedures = [
      ...allDocumentation.filter(item => 
        item.category?.includes('Policy') || 
        item.name?.toLowerCase().includes('policy')
      ),
      ...(requirements.procedures || []).filter(item =>
        item.category?.includes('Policy') || 
        item.name?.toLowerCase().includes('policy')
      )
    ];
    
    const operationalRestrictions = requirements.restrictions || [];
    
    // Additional Equipment Selected (beyond mandatory requirements)
    const additionalEquipment = [];
    
    // Check for optional equipment selections from vessel config
    if (vesselConfig) {
      // ===== NAVIGATION EQUIPMENT =====
      
      // Radar (when not mandatory for category)
      if (vesselConfig.hasRadar && vesselConfig.category > 2) {
        additionalEquipment.push({
          id: 'optional.radar',
          name: 'Radar (Optional)',
          category: 'Additional Navigation Equipment',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Marine radar system - optional for this vessel category'
        });
      }
      
      // ECDIS (when selected)
      if (vesselConfig.hasECDIS) {
        additionalEquipment.push({
          id: 'optional.ecdis',
          name: 'ECDIS (Optional)',
          category: 'Additional Navigation Equipment', 
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Electronic Chart Display & Information System - requires specific training'
        });
      }
      
      // AIS (when not mandatory)
      if (vesselConfig.hasAIS && (!vesselConfig.tonnage || vesselConfig.tonnage < 300) && !vesselConfig.carriesPassengers) {
        additionalEquipment.push({
          id: 'optional.ais',
          name: 'AIS Transceiver (Optional)',
          category: 'Additional Navigation Equipment',
          reference: 'Optional Selection', 
          mandatory: false,
          description: 'Automatic Identification System - optional for vessels <300 GT'
        });
      }
      
      // NAVTEX (when not mandatory for category)
      if (vesselConfig.hasNavtex && vesselConfig.category > 2) {
        additionalEquipment.push({
          id: 'optional.navtex',
          name: 'NAVTEX Receiver (Optional)',
          category: 'Additional Navigation Equipment',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Weather and navigation warnings receiver - optional for this category'
        });
      }
      
      // MF Radio (when not mandatory for category)
      if (vesselConfig.hasMF && vesselConfig.category > 1) {
        additionalEquipment.push({
          id: 'optional.mf_radio',
          name: 'MF/HF Radio (Optional)',
          category: 'Additional Communication Equipment',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Medium/High frequency radio - optional for this category'
        });
      }

      // Check optionalEquipment array for navigation items
      if (vesselConfig.optionalEquipment?.includes('gps_backup')) {
        additionalEquipment.push({
          id: 'optional.gps_backup',
          name: 'GPS Backup Unit (Optional)',
          category: 'Additional Navigation Equipment',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Secondary GPS for redundancy'
        });
      }

      if (vesselConfig.optionalEquipment?.includes('chart_plotter')) {
        additionalEquipment.push({
          id: 'optional.chart_plotter',
          name: 'Electronic Chart Plotter (Optional)',
          category: 'Additional Navigation Equipment',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Electronic navigation charts display (not IMO approved)'
        });
      }

      if (vesselConfig.optionalEquipment?.includes('autopilot')) {
        additionalEquipment.push({
          id: 'optional.autopilot',
          name: 'Autopilot (Optional)',
          category: 'Additional Navigation Equipment',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Automatic steering system'
        });
      }

      if (vesselConfig.optionalEquipment?.includes('gyro_compass')) {
        additionalEquipment.push({
          id: 'optional.gyro_compass',
          name: 'Gyro Compass (Optional)',
          category: 'Additional Navigation Equipment',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Non-magnetic compass system'
        });
      }

      if (vesselConfig.optionalEquipment?.includes('speed_log')) {
        additionalEquipment.push({
          id: 'optional.speed_log',
          name: 'Speed Log (Optional)',
          category: 'Additional Navigation Equipment',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Speed through water measurement'
        });
      }

      if (vesselConfig.optionalEquipment?.includes('night_vision')) {
        additionalEquipment.push({
          id: 'optional.night_vision',
          name: 'Night Vision/FLIR (Optional)',
          category: 'Additional Navigation Equipment',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Thermal imaging camera'
        });
      }

      if (vesselConfig.optionalEquipment?.includes('searchlight')) {
        additionalEquipment.push({
          id: 'optional.searchlight',
          name: 'Remote Searchlight (Optional)',
          category: 'Additional Navigation Equipment',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Remote controlled searchlight'
        });
      }

      // Communication Equipment
      if (vesselConfig.optionalEquipment?.includes('ssb_radio')) {
        additionalEquipment.push({
          id: 'optional.ssb_radio',
          name: 'SSB Radio (Optional)',
          category: 'Additional Communication Equipment',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Single sideband radio for long range'
        });
      }

      if (vesselConfig.optionalEquipment?.includes('sat_phone')) {
        additionalEquipment.push({
          id: 'optional.sat_phone',
          name: 'Satellite Phone (Optional)',
          category: 'Additional Communication Equipment',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Satellite communication system'
        });
      }

      if (vesselConfig.optionalEquipment?.includes('internet')) {
        additionalEquipment.push({
          id: 'optional.internet',
          name: 'Internet/4G/5G (Optional)',
          category: 'Additional Communication Equipment',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Internet connectivity system'
        });
      }

      if (vesselConfig.optionalEquipment?.includes('intercom')) {
        additionalEquipment.push({
          id: 'optional.intercom',
          name: 'Intercom System (Optional)',
          category: 'Additional Communication Equipment',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Internal communication system'
        });
      }

      if (vesselConfig.optionalEquipment?.includes('pa_system') && !vesselConfig.autoRequiredPASystem) {
        additionalEquipment.push({
          id: 'optional.pa_system',
          name: 'PA System (Optional)',
          category: 'Additional Communication Equipment',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Public address system'
        });
      }

      // ===== ENGINEERING & VESSEL SYSTEMS =====

      // Emergency Steering
      if (vesselConfig.hasEmergencySteering || vesselConfig.emergency_steering) {
        additionalEquipment.push({
          id: 'optional.emergency_steering',
          name: 'Emergency Steering (Optional)',
          category: 'Additional Engineering Systems',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Emergency steering arrangements'
        });
      }

      // Automatic Bilge System
      if (vesselConfig.bilge_auto) {
        additionalEquipment.push({
          id: 'optional.bilge_auto',
          name: 'Automatic Bilge System (Optional)',
          category: 'Additional Engineering Systems',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Bilge pumps operate automatically without crew intervention'
        });
      }

      // Emergency Generator
      if (vesselConfig.emergencyGenerator) {
        additionalEquipment.push({
          id: 'optional.emergency_generator',
          name: 'Emergency Generator (Optional)',
          category: 'Additional Engineering Systems',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Backup power generator fitted'
        });
      }

      // ===== ELECTRICAL SYSTEMS =====

      // Electrical Systems Installed
      if (vesselConfig.hasElectricalSystem) {
        additionalEquipment.push({
          id: 'optional.electrical_system',
          name: 'Electrical Systems (Optional)',
          category: 'Additional Electrical Systems',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Electrical systems beyond basic navigation lights'
        });
      }

      // Centralised Electrical System
      if (vesselConfig.hasCentralElectrical || vesselConfig.power_critical_systems) {
        additionalEquipment.push({
          id: 'optional.central_electrical',
          name: 'Centralised Electrical System (Optional)',
          category: 'Additional Electrical Systems',
          reference: 'WBC3 9.7.1.1',
          mandatory: false,
          description: 'Centralized electrical system requiring emergency backup'
        });
      }

      // Fixed Radio Equipment
      if (vesselConfig.hasFixedRadio) {
        additionalEquipment.push({
          id: 'optional.fixed_radio',
          name: 'Fixed Radio Equipment (Optional)',
          category: 'Additional Electrical Systems',
          reference: 'WBC3 9.7.2.1',
          mandatory: false,
          description: 'Fixed radio equipment requiring emergency power'
        });
      }

      // Electrical Navigation Equipment
      if (vesselConfig.hasElectricalNavEquipment) {
        additionalEquipment.push({
          id: 'optional.electrical_nav_equipment',
          name: 'Electrical Navigation Equipment (Optional)',
          category: 'Additional Electrical Systems',
          reference: 'WBC3 9.7.3.1',
          mandatory: false,
          description: 'Electrically powered navigation equipment'
        });
      }

      // Backup Battery Systems
      if (vesselConfig.hasBackupBatteries) {
        additionalEquipment.push({
          id: 'optional.backup_batteries',
          name: 'Backup Battery Systems (Optional)',
          category: 'Additional Electrical Systems',
          reference: 'WBC3 9.3.1.5',
          mandatory: false,
          description: 'Backup battery systems'
        });
      }

      // Ventilated Battery Types
      if (vesselConfig.hasVentilatedBatteries) {
        additionalEquipment.push({
          id: 'optional.ventilated_batteries',
          name: 'Ventilated Battery Types (Optional)',
          category: 'Additional Electrical Systems',
          reference: 'WBC3 9.3.2.2',
          mandatory: false,
          description: 'Ventilated battery types requiring drip trays'
        });
      }

      // Battery Charging System
      if (vesselConfig.hasBatteryCharger) {
        additionalEquipment.push({
          id: 'optional.battery_charger',
          name: 'Battery Charging System (Optional)',
          category: 'Additional Electrical Systems',
          reference: 'WBC3 9.3.1.3',
          mandatory: false,
          description: 'Battery charging system requiring overcharge protection'
        });
      }

      // High Voltage Systems
      if (vesselConfig.hasHighVoltage) {
        additionalEquipment.push({
          id: 'optional.high_voltage',
          name: 'High Voltage Systems (Optional)',
          category: 'Additional Electrical Systems',
          reference: 'WBC3 definition',
          mandatory: false,
          description: 'High voltage systems (>1000V AC / >1500V DC)'
        });
      }

      // ===== ACCOMMODATION & FIRE SAFETY =====

      // Accommodation Spaces
      if (vesselConfig.hasAccommodation) {
        additionalEquipment.push({
          id: 'optional.accommodation',
          name: 'Accommodation Spaces (Optional)',
          category: 'Additional Fire Safety & Accommodation',
          reference: 'WBC3 16.2.1.2',
          mandatory: false,
          description: 'Accommodation spaces requiring fire protection'
        });
      }

      // Sleeping Accommodation
      if (vesselConfig.hasSleeping) {
        additionalEquipment.push({
          id: 'optional.sleeping',
          name: 'Sleeping Accommodation (Optional)',
          category: 'Additional Fire Safety & Accommodation',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Sleeping accommodation requiring fire protection'
        });
      }

      // NOTE: Cooking Facilities and Gas Appliances are NOT listed here when they come from
      // galley configuration, as they're already fully represented by proper FFE requirements
      // (fire detectors, extinguishers, etc.) in the main requirements section.
      // Only show these if they're additional/separate from the main galley setup.

      // Heating Systems (separate from cooking)
      if (vesselConfig.hasHeating) {
        additionalEquipment.push({
          id: 'optional.heating',
          name: 'Heating Systems (Optional)',
          category: 'Additional Fire Safety & Accommodation',
          reference: 'Optional Selection',
          mandatory: false,
          description: 'Heating systems requiring fire protection'
        });
      }

      // Fire Buckets (WBC3 16.6.1) - Manual selections
      if (vesselConfig.isPracticable || vesselConfig.deemedNecessary || vesselConfig.companyRequiresBuckets) {
        const reason = vesselConfig.isPracticable ? 'practicable per WBC3 16.6.1' :
                       vesselConfig.deemedNecessary ? 'deemed necessary by Certifying Authority' :
                       'required by company SMS';

        additionalEquipment.push({
          id: 'optional.fire_buckets',
          name: 'Fire Buckets',
          category: 'Additional Fire Safety & Accommodation',
          reference: 'WBC3 16.6.1',
          mandatory: false, // It's a manual selection, not automatically mandatory
          description: `Fire buckets of at least 9 litres capacity - ${reason}`
        });
      }

      // ===== DANGEROUS GOODS & SPECIAL OPERATIONS =====

      // Dangerous Goods
      if (vesselConfig.hasDangerousGoods) {
        additionalEquipment.push({
          id: 'optional.dangerous_goods',
          name: 'Dangerous Goods Operations (Optional)',
          category: 'Additional Special Operations',
          reference: 'WBC3 29',
          mandatory: false,
          description: 'Carries dangerous goods requiring additional fire protection'
        });
      }
    }
    
    return {
      equipmentRequirements,
      additionalEquipment,
      crewQualificationsAndCertifications,
      vesselDocumentationAndRecords,
      operationalProcedures,
      trainingRequirements,
      riskAssessments,
      policiesAndProcedures,
      operationalRestrictions
    };
  }, [requirements, vesselConfig]);

  useEffect(() => {
    // Get vessel requirements from hardcoded JS functions (like maintenance system)
    try {
      setLoading(true);
      setError(null);
      
      // Debug logging to track category changes

      const reqs = getHardcodedVesselRequirements(vesselConfig);
      
      // Debug crew documentation specifically
      const crewDocs = reqs.documentation?.filter(doc => doc.id?.startsWith('crew.doc.master')) || [];

      // Debug procedures and risk assessments

      reqs.procedures?.forEach(proc => {

      });

      reqs.riskAssessments?.forEach(risk => {

      });
      
      setRequirements(reqs);
    } catch (err) {

      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [vesselConfig]);

  if (loading) {
    return <div className="p-4">Loading requirements...</div>;
  }

  // Option A: Strict module alignment sections
  const sections = [
    {
      key: 'equipmentRequirements',
      title: '🔧 Equipment Requirements',
      icon: Shield,
      color: 'blue',
      empty: 'No equipment requirements beyond basic vessel equipment',
      module: 'Equipment Register'
    },
    {
      key: 'additionalEquipment',
      title: '➕ Additional Equipment Selected',
      icon: Shield,
      color: 'cyan',
      empty: 'No additional equipment selected beyond mandatory requirements',
      module: 'Equipment Register'
    },
    {
      key: 'crewQualificationsAndCertifications',
      title: '👥 Crew Qualifications & Certifications',
      icon: Award,
      color: 'teal',
      empty: 'No crew qualifications or certifications required',
      module: 'Crew Certification'
    },
    {
      key: 'vesselDocumentationAndRecords',
      title: '📄 Vessel Documentation & Records',
      icon: FileText,
      color: 'gray',
      empty: 'No vessel documentation requirements',
      module: 'Documentation'
    },
    {
      key: 'operationalProcedures',
      title: '📋 Operational Procedures',
      icon: Clipboard,
      color: 'green',
      empty: 'No operational procedures required',
      module: 'Procedures'
    },
    {
      key: 'trainingRequirements',
      title: '🎓 Training Requirements',
      icon: Book,
      color: 'yellow',
      empty: 'No training requirements beyond basic crew training',
      module: 'Training'
    },
    {
      key: 'riskAssessments',
      title: '⚠️ Risk Assessments',
      icon: AlertCircle,
      color: 'red',
      empty: 'No risk assessments required',
      module: 'Risk Assessments'
    },
    {
      key: 'policiesAndProcedures',
      title: '📑 Policies & Procedures',
      icon: Database,
      color: 'indigo',
      empty: 'No policies or procedures required',
      module: 'Policies & Procedures'
    },
    {
      key: 'operationalRestrictions',
      title: '🚫 Operational Restrictions',
      icon: Ban,
      color: 'purple',
      empty: 'No operational restrictions apply',
      module: 'Informational'
    }
  ];

  // Count total requirements using processed requirements
  const totalRequirements = processedRequirements ? Object.values(processedRequirements).reduce(
    (sum, category) => Array.isArray(category) ? sum + category.length : sum, 0
  ) : 0;

  return (
    <div className="space-y-6">
      {/* Debug Toggle */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setShowDebug(!showDebug)}
          className="text-xs text-cyan-600 hover:text-cyan-500"
        >
          {showDebug ? 'Hide' : 'Show'} Debug Info
        </button>
      </div>

      {/* Debug View */}
      {showDebug && <RequirementsDebug vesselConfig={vesselConfig} />}

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900">Requirements Summary</h3>
        <p className="mt-1 text-sm text-blue-700">
          {vesselConfig.operatingStatus === 'inland' ? 'Inland Waterways' : 'WBC3 (Seagoing)'} Requirements: 
          {totalRequirements} requirements have been identified.
        </p>
        {totalRequirements > 0 && (
          <p className="mt-2 text-xs text-blue-600">
            These are based on your vessel configuration and operating status.
          </p>
        )}
      </div>

      {/* Requirements by Category */}
      {processedRequirements && sections.map(section => {
        const Icon = section.icon;
        const items = processedRequirements[section.key] || [];
        const hasItems = items.length > 0;
        
        return (
          <div key={section.key} className="space-y-3">
            <div className="flex items-center gap-2">
              <Icon className={`h-5 w-5 text-${section.color}-600`} />
              <div className="flex-1">
                <h3 className="text-base font-medium text-gray-900">{section.title}</h3>
                <p className="text-xs text-gray-500">→ {section.module} module</p>
              </div>
              {hasItems && (
                <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-${section.color}-100 text-${section.color}-800`}>
                  {items.length}
                </span>
              )}
            </div>
            
            {hasItems ? (
              <div className="space-y-2">
                {items.map((item, index) => {
                  // Check if this is an open-flame protection requirement and show evaluation
                  const isOpenFlameProtection = item.id === 'ffe_protection_open_flame' && openFlameEvaluation;
                  
                  // Check for engine box protection evaluation
                  const isEngineBoxProtection = item.id === 'ffe_extinguisher_enginebox_above' && item.evaluation;
                  const engineBoxEvaluation = item.evaluation;
                  
                  return (
                    <div 
                      key={item.id || index}
                      className={`border border-${section.color}-200 bg-${section.color}-50 rounded-lg p-3`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-start gap-2">
                            <h4 className="text-sm font-medium text-gray-900">
                              {item.name}
                            </h4>
                            {(isOpenFlameProtection || isEngineBoxProtection) && (
                              <div className={`flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                                (isOpenFlameProtection ? openFlameEvaluation.status : engineBoxEvaluation?.fulfilledBy?.length > 0 ? 'meets' : 'missing') === 'meets' 
                                  ? 'bg-green-100 text-green-800' 
                                  : (isOpenFlameProtection ? openFlameEvaluation.status : 'missing') === 'partial' 
                                  ? 'bg-yellow-100 text-yellow-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {(isOpenFlameProtection ? openFlameEvaluation.status : engineBoxEvaluation?.fulfilledBy?.length > 0 ? 'meets' : 'missing') === 'meets' && <CheckCircle className="w-3 h-3" />}
                                {(isOpenFlameProtection ? openFlameEvaluation.status : 'missing') === 'partial' && <AlertTriangle className="w-3 h-3" />}
                                {(isOpenFlameProtection ? openFlameEvaluation.status : engineBoxEvaluation?.fulfilledBy?.length > 0 ? 'meets' : 'missing') === 'missing' && <XCircle className="w-3 h-3" />}
                                {(isOpenFlameProtection ? openFlameEvaluation.status : engineBoxEvaluation?.fulfilledBy?.length > 0 ? 'meets' : 'missing') === 'meets' ? 'Compliant' : 
                                 (isOpenFlameProtection ? openFlameEvaluation.status : 'missing') === 'partial' ? 'Partial' : 'Not Compliant'}
                              </div>
                            )}
                          </div>
                          {item.description && (
                            <p className="mt-1 text-xs text-gray-600">{item.description}</p>
                          )}
                          
                          {/* Show open-flame evaluation details */}
                          {isOpenFlameProtection && (
                            <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                              <div className="text-xs space-y-1">
                                <div className="font-medium text-gray-700">Requirements (WBC3 16.3.1):</div>
                                <div className="text-gray-600">• Base: 5A/34B extinguisher (standard galley requirement) {openFlameEvaluation.selected.baseExt ? '✓' : '✗'}</div>
                                <div className="text-gray-600">• Plus ONE of these additional protections:</div>
                                <div className="ml-4 text-gray-600">
                                  - Additional 8A/68B extinguisher (beyond base) {openFlameEvaluation.selected.extraExt ? '✓' : '✗'}
                                </div>
                                <div className="ml-4 text-gray-600">
                                  - Fire blanket {openFlameEvaluation.selected.blanket ? '✓' : '✗'}
                                </div>
                                <div className="ml-4 text-gray-600">
                                  - Fixed galley suppression system (per 16.4) {openFlameEvaluation.selected.fixed ? '✓' : '✗'}
                                </div>
                                {openFlameEvaluation.fulfilledBy.length > 0 && (
                                  <div className="mt-1 pt-1 border-t text-green-700 font-medium">
                                    ✓ {openFlameEvaluation.fulfilledBy.join(', ')}
                                  </div>
                                )}
                                {openFlameEvaluation.reasons.length > 0 && openFlameEvaluation.status !== 'meets' && (
                                  <div className="mt-1 pt-1 border-t text-amber-700">
                                    ⚠ {openFlameEvaluation.reasons.join('; ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Show engine box protection evaluation details */}
                          {isEngineBoxProtection && (
                            <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                              <div className="text-xs space-y-1">
                                <div className="font-medium text-gray-700">Requirements (WBC3 Table 16.1.1.1):</div>
                                <div className="text-gray-600">• Select ONE protection method for above deck engine:</div>
                                <div className="ml-4 text-gray-600">
                                  - Portable extinguishers to fire port {engineBoxEvaluation?.selected?.firePort ? '✓' : '✗'}
                                </div>
                                <div className="ml-4 text-gray-600">
                                  - Fixed fire extinguishing system {engineBoxEvaluation?.selected?.fixedSystem ? '✓' : '✗'}
                                </div>
                                {engineBoxEvaluation?.fulfilledBy?.length > 0 && (
                                  <div className="mt-1 pt-1 border-t text-green-700 font-medium">
                                    ✓ {engineBoxEvaluation.fulfilledBy.join(', ')}
                                  </div>
                                )}
                                {(!engineBoxEvaluation?.fulfilledBy || engineBoxEvaluation.fulfilledBy.length === 0) && (
                                  <div className="mt-1 pt-1 border-t text-amber-700">
                                    ⚠ No protection method selected - please select one option
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {item.note && (
                            <p className="mt-1 text-xs text-gray-500 italic">{item.note}</p>
                          )}
                          {item.items && (
                            <ul className="mt-2 text-xs text-gray-600 list-disc list-inside">
                              {item.items.map((subItem, idx) => (
                                <li key={idx}>{subItem}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div className="ml-3 text-right">
                          <span className="text-xs text-gray-500">{item.reference}</span>
                          {item.priority && (
                            <div className={`mt-1 text-xs font-medium ${
                              item.priority === 'critical' ? 'text-red-600' :
                              item.priority === 'required' ? 'text-orange-600' :
                              'text-gray-600'
                            }`}>
                              {item.priority.toUpperCase()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">{section.empty}</p>
            )}
          </div>
        );
      })}

      {/* Export Options */}
      <div className="pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            This requirements list can be exported for compliance documentation
          </p>
          <button
            type="button"
            onClick={() => {
              // Create a text summary of requirements
              let summary = `Vessel Requirements Summary\n`;
              summary += `Generated: ${new Date().toLocaleDateString()}\n`;
              summary += `Vessel Type: ${vesselConfig.vesselType || 'Not specified'}\n`;
              summary += `Max Speed: ${vesselConfig.maxSpeed || 'Not specified'} knots\n`;
              summary += `Area Category: ${vesselConfig.category || 'Not specified'}\n\n`;
              
              sections.forEach(section => {
                const items = processedRequirements[section.key] || [];
                if (items.length > 0) {
                  summary += `${section.title}:\n`;
                  items.forEach(item => {
                    summary += `- ${item.name} (${item.reference})\n`;
                  });
                  summary += '\n';
                }
              });
              
              // Download as text file
              const blob = new Blob([summary], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `vessel-requirements-${Date.now()}.txt`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="px-3 py-1.5 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700"
          >
            Export Requirements
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequirementsTab;